import React from "react";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { getSnackbarOptions } from "../../../../../../../lib/getSnackbarOptions";
import {
  Dialog,
  LinearProgress,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
} from "@mui/material";
import { Skeleton } from "@mui/material";
import { Step } from "../../../../../../../models/step/step";
import { useApi } from "../../../../../../common/providers/Api.provider";
import { useTranslation } from "react-i18next";

export interface StepDeleteDialogComponentProps {
  open: boolean;
  step?: Step;
  handleClose: (result?: number) => void;
}

export function StepDeleteDialogComponent({
  open,
  step,
  handleClose,
}: StepDeleteDialogComponentProps) {
  const { t: questionnaireTranslations } = useTranslation(
    "questionnaire-page",
    {
      keyPrefix: "steps.dialogs.delete",
      useSuspense: false,
    }
  );
  const { t: commonTranslations } = useTranslation("common", {
    useSuspense: false,
  });

  const api = useApi();
  const snackbar = useSnackbar();

  const [isLoadingState, setIsLoadingState] = useState<boolean>(false);

  const [nameState, setNameState] = useState<string>("");

  const handleDelete = async (step?: Step) => {
    if (!step || !step.id) {
      return;
    }

    try {
      setIsLoadingState(true);

      const response = await api.stepsDelete(step.id);

      if (response.success) {
        snackbar.enqueueSnackbar(
          response.message,
          getSnackbarOptions({ variant: "success" })
        );
      } else {
        snackbar.enqueueSnackbar(
          response.message,
          getSnackbarOptions({ variant: "error" })
        );
      }

      handleClose(step.id);
    } catch (e) {
      snackbar.enqueueSnackbar(
        e as String,
        getSnackbarOptions({
          variant: "error",
        })
      );
    } finally {
      setNameState("");
      setIsLoadingState(false);
    }
  };

  return (
    <Dialog open={open}>
      {isLoadingState && <LinearProgress />}
      <DialogTitle>
        {!step ? (
          <Skeleton variant="text" />
        ) : (
          questionnaireTranslations("title", { step: step.name })
        )}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {!step ? (
            <Skeleton variant="rectangular" />
          ) : (
            questionnaireTranslations("description", { step: step.name })
          )}
        </DialogContentText>
        {!step ? (
          <Skeleton variant="text" />
        ) : (
          <TextField
            autoFocus
            fullWidth
            required
            autoComplete="off"
            variant="outlined"
            margin="dense"
            id="name"
            label={questionnaireTranslations("repeatName")}
            type="text"
            value={nameState}
            onChange={(event) => setNameState(event.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        {!step ? (
          <Skeleton variant="rectangular" />
        ) : (
          <Button onClick={() => handleClose(undefined)} color="secondary">
            {commonTranslations("cancel")}
          </Button>
        )}
        {!step ? (
          <Skeleton variant="rectangular" />
        ) : (
          <Button
            disabled={!step || step.name !== nameState}
            onClick={() => handleDelete(step)}
            color="primary"
          >
            {commonTranslations("remove")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
